import { useCallback, useRef } from 'react';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useQueryClient } from '@tanstack/react-query';
import { brandConfig } from '../../config/brands';
import { replaceTerms } from '../../manual/glossary';
import { markNavigationGuideComplete } from '../profile/api';
import './NavigationGuide.css';

const POPOVER_CLASS = 'saite-guide-popover';

function buildSteps(): DriveStep[] {
  const t = (s: string) => replaceTerms(s);

  return [
    {
      element: '[data-guide-step="hero"]',
      popover: {
        title: t('{{serviceName}} へようこそ'),
        description: t(
          '{{serviceName}} の基本的な使い方を簡単にご案内します。静止画・動画の生成方法から{{products}}管理までをご説明します。',
        ),
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="image-gen-card"]',
      popover: {
        title: '静止画生成',
        description: t(
          'プロンプト不要の{{attributeMode}}とプロンプト入力モードの2モードから、用途に合わせてご選択いただきご利用ください。',
        ),
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="video-gen-card"]',
      popover: {
        title: '動画生成',
        description: t(
          'プロンプト不要の{{scenarioMode}}とプロンプト入力モードの2モードから、用途に合わせてご選択いただきご利用ください。',
        ),
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="prompt-input"]',
      popover: {
        title: 'プロンプト入力',
        description: t(
          'プロンプト入力で生成する場合は、TOPページから{{materials}}をアップロードまたは選択した上でプロンプトを入力し、生成することも可能です。',
        ),
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="type-toggle"]',
      popover: {
        title: '静止画 / 動画の切り替え',
        description: '生成したいフォーマットを静止画・動画からご選択ください。',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="detail-settings"]',
      popover: {
        title: '詳細設定',
        description:
          'モデル選択や媒体要件 (アスペクト比・解像度・プラットフォーム) を細かく指定できます。',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="generate-button"]',
      popover: {
        title: '生成する',
        description:
          'プロンプトを入力したらこのボタンで生成を開始します。ボタン横のバッジは消費ポイントの目安です。',
        side: 'top',
        align: 'end',
      },
    },
    {
      element: '[data-guide-step="recent-projects"]',
      popover: {
        title: t('最近の{{products}}'),
        description: t(
          '過去に作成・登録した{{products}}にすぐ戻れます。「新規{{products}}を登録」から、商品情報をベースにした生成も始められます。',
        ),
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-guide-step="nav-studio"]',
      popover: {
        title: t('{{studio}}'),
        description: t(
          '静止画・動画の詳細生成画面に移行します。{{products}}・{{materials}}を追加・選択して各モードでの生成が可能です。',
        ),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-guide-step="nav-materials"]',
      popover: {
        title: t('{{materials}}'),
        description: t('登録した{{materials}}や生成した{{materials}}の管理が可能です。'),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-guide-step="nav-products"]',
      popover: {
        title: t('{{products}}'),
        description: 'ブランド・商品・案件単位での管理が可能です。',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-guide-step="nav-actors"]',
      popover: {
        title: t('{{aiActors}}'),
        description: t(
          '{{serviceName}} に登録済みの{{aiActors}}管理が可能です。ご自身で新たに専属{{aiActors}}の生成も可能です。',
        ),
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-guide-step="header-menu"]',
      popover: {
        title: '各種設定',
        description:
          'プラン管理・組織・ユーザー管理・ブランド管理など、様々な管理と設定が可能です。',
        side: 'left',
        align: 'start',
      },
    },
  ];
}

/**
 * driver.js を使った SAITE 向けナビゲーションガイド
 * - start(): 強制的に最初から起動 (再生用)
 * - destroy(): 必要に応じて停止
 */
export function useNavigationGuide() {
  const queryClient = useQueryClient();
  const driverRef = useRef<Driver | null>(null);
  const completedRef = useRef(false);

  const start = useCallback(() => {
    // 既存インスタンスを破棄
    driverRef.current?.destroy();
    completedRef.current = false;

    const handleFinish = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      markNavigationGuideComplete()
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        })
        .catch(() => {
          // ガイド自体は閉じる。サーバー記録に失敗しても致命ではない
        });
    };

    const instance = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: POPOVER_CLASS,
      progressText: '{{current}} / {{total}}',
      nextBtnText: '次へ',
      prevBtnText: '戻る',
      doneBtnText: '完了',
      steps: buildSteps(),
      onDestroyStarted: () => {
        // ユーザーが ESC や ✕ で閉じた場合も完了扱いにする (再表示しない)
        handleFinish();
        instance.destroy();
      },
      onDestroyed: () => {
        handleFinish();
      },
    });

    driverRef.current = instance;
    instance.drive();
  }, [queryClient]);

  return { start };
}

export const navigationGuideEnabled = (): boolean => brandConfig.features.navigationGuide;
